'use client'

import { cn } from '../../helpers/index.js'
import { Heading, Text, Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/index.js'
import type { CartLineItem, Cart } from './index.js'

/**
 * 购物车数据类型 - 复用 MiniCart 的类型定义
 */
export interface CartData {
  id: string
  checkoutUrl: string
  lineItems: CartLineItem[]
}

// 重新导出类型供外部使用
export type { CartLineItem, Cart }

/**
 * 文案配置
 */
export interface MiniCartDialogCopy {
  /** 标题文案，{count} 会被替换为商品数量 */
  title?: string
  /** 空购物车提示 */
  emptyText?: string
}

export type MiniCartDialogSemanticName =
  | 'root'
  | 'header'
  | 'title'
  | 'content'
  | 'itemList'
  | 'item'
  | 'itemImage'
  | 'itemInfo'
  | 'itemTitle'
  | 'itemPrice'
  | 'itemCurrentPrice'
  | 'itemOriginalPrice'
  | 'quantityControl'
  | 'quantityButton'
  | 'quantityValue'

export interface MiniCartDialogProps {
  /** 是否打开弹窗 */
  open: boolean
  /** 关闭弹窗回调 */
  onOpenChange: (open: boolean) => void
  /** 购物车数据 */
  cart: CartData
  /** 文案配置 */
  copy?: MiniCartDialogCopy
  /** 各部分的自定义样式类名 */
  classNames?: Partial<Record<MiniCartDialogSemanticName, string>>
  /**
   * 增加商品数量回调
   * @param lineItemId 商品行ID
   * @param currentQuantity 当前数量
   */
  onIncreaseQuantity?: (lineItemId: string, currentQuantity: number) => void
  /**
   * 减少商品数量回调
   * @param lineItemId 商品行ID
   * @param currentQuantity 当前数量
   */
  onDecreaseQuantity?: (lineItemId: string, currentQuantity: number) => void
  /**
   * 数量变更回调
   * @param lineItemId 商品行ID
   * @param quantity 新数量
   */
  onQuantityChange?: (lineItemId: string, quantity: number) => void
}

/**
 * MinusIcon - 减号图标
 */
const MinusIcon = ({ className }: { className?: string }) => (
  <svg className={className} width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 6H10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

/**
 * PlusIcon - 加号图标
 */
const PlusIcon = ({ className }: { className?: string }) => (
  <svg className={className} width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 2V10M2 6H10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

/**
 * CartItem - 购物车商品项组件
 */
interface CartItemProps {
  item: CartLineItem
  classNames?: Partial<Record<MiniCartDialogSemanticName, string>>
  onIncrease?: () => void
  onDecrease?: () => void
}

const CartItem = ({ item, classNames, onIncrease, onDecrease }: CartItemProps) => {
  const { merchandise, quantity, cost } = item
  const { image, title } = merchandise
  const { totalAmount, subtotalAmount } = cost

  // 显示标题（回退到 SKU）
  const displayTitle = title || merchandise.sku || 'Product'
  // 显示价格（现价 = totalAmount）
  const displayPrice = totalAmount?.formattedPrice || ''
  // 是否有折扣（原价 subtotalAmount 大于现价 totalAmount）
  const hasDiscount =
    subtotalAmount && totalAmount && parseFloat(subtotalAmount.amount) > parseFloat(totalAmount.amount)

  return (
    <div
      className={cn(
        // 移动端：灰色背景，较小间距
        'bg-container-primary rounded-box flex items-center gap-2 px-2 py-4',
        // 桌面端：白色背景，较大间距
        'laptop:bg-container-primary laptop:gap-4 laptop:p-4',
        classNames?.item
      )}
    >
      {/* 商品图片 - 移动端 64px，桌面端 80px */}
      <div
        className={cn('rounded-box relative size-16 shrink-0 overflow-hidden', 'laptop:size-20', classNames?.itemImage)}
      >
        {image?.url && <img src={image.url} alt={image.altText || displayTitle} className="size-full object-cover" />}
      </div>

      {/* 商品信息 */}
      <div className={cn('laptop:gap-4 flex flex-1 flex-col gap-2', classNames?.itemInfo)}>
        {/* 商品名称 - 移动端较小字体 */}
        <Text
          as="p"
          size={3}
          className={cn('text-info-primary laptop:text-[16px] line-clamp-2 text-[14px]', classNames?.itemTitle)}
        >
          {displayTitle}
        </Text>

        {/* 价格和数量控制 - 移动端垂直排列，桌面端水平排列 */}
        <div className="laptop:flex-row laptop:items-center laptop:justify-between flex flex-col items-start gap-1">
          {/* 价格区域 */}
          <div className={cn('flex items-center gap-1', classNames?.itemPrice)}>
            {displayPrice && (
              <Text
                size={4}
                className={cn('text-info-primary laptop:text-[18px] text-[16px]', classNames?.itemCurrentPrice)}
              >
                {displayPrice}
              </Text>
            )}
            {hasDiscount && subtotalAmount?.formattedPrice && (
              <Text
                size={4}
                className={cn(
                  'text-info-tertiary laptop:text-[18px] text-[16px] line-through',
                  classNames?.itemOriginalPrice
                )}
              >
                {subtotalAmount.formattedPrice}
              </Text>
            )}
          </div>

          {/* 数量控制 - 移动端 32px 按钮，桌面端 40px 按钮 */}
          <div className={cn('flex items-center', classNames?.quantityControl)}>
            {/* 减少按钮 */}
            <button
              type="button"
              onClick={onDecrease}
              disabled={quantity <= 0}
              className={cn(
                'border-lines flex size-8 items-center justify-center border',
                'laptop:size-10',
                'text-info-primary hover:bg-container-secondary-0 transition-colors',
                'disabled:cursor-not-allowed disabled:opacity-50',
                classNames?.quantityButton
              )}
              aria-label="Decrease quantity"
            >
              <MinusIcon className="laptop:size-3 size-[10.67px]" />
            </button>

            {/* 数量显示 */}
            <div
              className={cn(
                'border-lines text-info-primary flex size-8 items-center justify-center border-y text-center text-sm font-semibold',
                'laptop:size-10 laptop:text-base',
                classNames?.quantityValue
              )}
            >
              {quantity}
            </div>

            {/* 增加按钮 */}
            <button
              type="button"
              onClick={onIncrease}
              className={cn(
                'border-lines flex size-8 items-center justify-center border',
                'laptop:size-10',
                'text-info-primary hover:bg-container-secondary-0 transition-colors',
                classNames?.quantityButton
              )}
              aria-label="Increase quantity"
            >
              <PlusIcon className="laptop:size-3 size-[10.67px]" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * MiniCartDialog - 迷你购物车弹窗组件
 *
 * @description 用于展示购物车商品列表，支持调整商品数量
 */
const MiniCartDialog = ({
  open,
  onOpenChange,
  cart,
  copy,
  classNames = {},
  onIncreaseQuantity,
  onDecreaseQuantity,
  onQuantityChange,
}: MiniCartDialogProps) => {
  // 文案配置
  const titleText = copy?.title || 'Selected ({count})'
  const emptyText = copy?.emptyText || 'Your cart is empty'

  // 计算商品总数量
  const totalQuantity = cart?.lineItems?.reduce((total, item) => total + (item?.quantity || 0), 0) || 0

  const items = cart?.lineItems || []
  const isEmpty = items.length === 0

  // 处理数量增加
  const handleIncrease = (lineItemId: string, currentQuantity: number) => {
    onIncreaseQuantity?.(lineItemId, currentQuantity)
    onQuantityChange?.(lineItemId, currentQuantity + 1)
  }

  // 处理数量减少
  const handleDecrease = (lineItemId: string, currentQuantity: number) => {
    if (currentQuantity > 0) {
      onDecreaseQuantity?.(lineItemId, currentQuantity)
      onQuantityChange?.(lineItemId, currentQuantity - 1)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          // 基础样式 + 居中定位
          'bg-container-secondary-0 fixed left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col gap-0 overflow-hidden p-0',
          // 移动端：居中弹窗，最小宽度326px，左右距屏幕边缘32px
          'rounded-box max-h-[80vh] w-[calc(100%-64px)] min-w-[326px]',
          // 桌面端：最大宽度560px
          'laptop:w-full laptop:max-w-[560px] laptop:rounded-box',
          classNames?.root
        )}
      >
        {/* 头部 */}
        <DialogHeader
          className={cn(
            'bg-container-secondary-0 flex flex-row items-start justify-between gap-4 px-4 pb-2 pt-4',
            classNames?.header
          )}
        >
          <DialogTitle asChild>
            <Heading as="h3" size={2} className={cn('text-info-primary font-bold', classNames?.title)}>
              {titleText.replace('{count}', totalQuantity.toString())}
            </Heading>
          </DialogTitle>
        </DialogHeader>

        {/* 商品列表 */}
        <div
          className={cn(
            'bg-container-secondary-0 h- flex flex-col gap-2 overflow-y-auto px-4 py-2',
            classNames?.content
          )}
        >
          {isEmpty ? (
            <div className="flex items-center justify-center py-8">
              <Text size={2} className="text-info-tertiary">
                {emptyText}
              </Text>
            </div>
          ) : (
            <div className={cn('flex flex-col gap-2', classNames?.itemList)}>
              {items.map(item => (
                <CartItem
                  key={item.id}
                  item={item}
                  classNames={classNames}
                  onIncrease={() => handleIncrease(item.id, item.quantity)}
                  onDecrease={() => handleDecrease(item.id, item.quantity)}
                />
              ))}
            </div>
          )}
        </div>

        {/* 底部安全区域 */}
        <div className="bg-container-secondary-0 h-8 w-full" />
      </DialogContent>
    </Dialog>
  )
}

MiniCartDialog.displayName = 'MiniCartDialog'

export default MiniCartDialog
export { MiniCartDialog }
